import React, { useMemo, useState } from "react";
import axios from "axios";
import { Stepper, Group, Button, Stack, Text, Alert, Divider, Paper, SimpleGrid, } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import { ExperienceInitResponseDto, CreateExperienceBookingRequestDto } from "./types";
import { Select, TextInput } from "@mantine/core"; // tilføj disse
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";
import { getAuthHeaders } from "../../api/authHelper"; // tilpas stien

type Props = {
    init: ExperienceInitResponseDto;
    token: string;
};

type Draft = {
    pickupLocationId?: number;
    dropoffLocationId?: number;
    pickupDate?: string;
    pickupTime?: string;
    passengerCount: number;

    whatsappCountryIso2?: string;
    whatsappCountryCode?: string;   // fx "+45"
    whatsappNumber?: string;        // kun selve nummeret
};



function OptionGrid({ options, value, onChange, cols,
}: {
    options: { value: string; label: string }[];
    value: string | null;
    onChange: (v: string) => void;
    cols?: { base: number; sm: number };
}) {
    return (
        <SimpleGrid cols={cols ?? { base: 1, sm: 2 }} spacing="sm">
            {options.map((o) => {
                const selected = value === o.value;

                return (
                    <Paper
                        key={o.value}
                        withBorder
                        p="md"
                        radius="md"
                        style={{
                            cursor: "pointer",
                            borderColor: selected
                                ? "var(--mantine-primary-color-filled)"
                                : undefined,
                            boxShadow: selected
                                ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                : undefined,
                        }}
                        onClick={() => onChange(o.value)}
                    >
                        <Text fw={600}>{o.label}</Text>
                    </Paper>
                );
            })}
        </SimpleGrid>
    );
}

export default function ExperienceBookingStepper({ init, token }: Props) {
    const [active, setActive] = useState(0);
    const [draft, setDraft] = useState<Draft>({ passengerCount: 1 });
    const [times, setTimes] = useState<string[]>([]);
    const [loadingTimes, setLoadingTimes] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [useCustomPassengerCount, setUseCustomPassengerCount] =
        useState(false);
    const [customPassengerCount, setCustomPassengerCount] =
        useState<number | null>(null);

    const isMobile = useMediaQuery("(max-width: 768px)");

    const PRIORITY_ISO2 = [
        "DK",
        "CA",
        "CN",
        "FR",
        "DE",
        "HK",
        "IN",
        "ID",
        "JP",
        "MY",
        "MX",
        "SG",
        "ES",
        "TW",
        "TH",
        "TR",
        "GB",
        "VN",
        "BG",
    ];
    const pickupOptions = useMemo(
        () =>
            init.pickupLocations.map((l) => ({
                value: String(l.id),
                label: l.name,
            })),
        [init.pickupLocations]
    );

    const dropoffOptions = useMemo(
        () =>
            init.dropoffLocations.map((l) => ({
                value: String(l.id),
                label: l.name,
            })),
        [init.dropoffLocations]
    );

    const availableDates = useMemo(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        // ✅ No same-day booking: earliest day is tomorrow
        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(todayStart.getDate() + 1);

        const max = new Date(todayStart);
        max.setDate(todayStart.getDate() + init.bookingMaxDaysAhead);

        return init.timeWindows
            .map((w) => w.date)
            .filter((dateStr) => {
                const d = new Date(dateStr + "T00:00:00");
                return d >= tomorrowStart && d <= max;
            });
    }, [init.timeWindows, init.bookingMaxDaysAhead]);

    const dateOptions = useMemo(
        () => availableDates.map((d) => ({ value: d, label: d })),
        [availableDates]
    );

    const pickupName =
        init.pickupLocations.find((x) => x.id === draft.pickupLocationId)
            ?.name ?? null;


    const dropoffName =
        init.dropoffLocations.find((x) => x.id === draft.dropoffLocationId)
            ?.name ?? null;

    const passengersValid =
        !useCustomPassengerCount
            ? draft.passengerCount >= 1 && draft.passengerCount <= 30
            : customPassengerCount !== null && customPassengerCount >= 31;

    const whatsappCountryOptions = useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES
            .filter((c) => prioritySet.has(c.iso2))
            .sort((a, b) => PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES
            .filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: "Popular", items: popularItems },
            { group: "All countries", items: restItems },
        ];
    }, []);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.code === draft.whatsappCountryCode)?.country ?? null;

    const whatsappValid =
        !!draft.whatsappCountryCode &&
        !!draft.whatsappNumber &&
        draft.whatsappNumber.replace(/\s+/g, "").length >= 6; // “good enough” basic validation

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return whatsappValid;   // <--- NY
            case 1:
                return !!draft.pickupLocationId;
            case 2:
                return !!draft.dropoffLocationId;
            case 3:
                return !!draft.pickupDate;
            case 4:
                return !!draft.pickupTime;
            case 5:
                return passengersValid;
            default:
                return true;
        }
    };

    const ensureTimeslots = async () => {
        if (!draft.pickupDate) return;

        try {
            setLoadingTimes(true);
            const apiUrl = process.env.REACT_APP_API_URL;

            const res = await axios.get(
                `${apiUrl}/api/experience/booking/timeslots`,
                {
                    params: { token, date: draft.pickupDate },
                }
            );
            setTimes(res.data.times ?? []);
        } catch (e: any) {
            setError(e?.response?.data ?? "Kunne ikke hente tider.");
        } finally {
            setLoadingTimes(false);
        }
    };

    const next = async () => {
        if (active === 3) await ensureTimeslots();
        if (active < 6 && isStepValid(active))
            setActive((a) => a + 1);
    };

    const prev = () =>
        setActive((a) => Math.max(0, a - 1));

    const submit = async () => {
        if (
            !draft.pickupLocationId ||
            !draft.dropoffLocationId ||
            !draft.pickupDate ||
            !draft.pickupTime
        ) {
            setError("Mangler data i bestillingen.");
            return;
        }

        const payload: CreateExperienceBookingRequestDto = {
            token,
            pickupLocationId: draft.pickupLocationId,
            dropoffLocationId: draft.dropoffLocationId,
            pickupDate: draft.pickupDate,
            pickupTime: draft.pickupTime,
            passengerCount: draft.passengerCount,

            WhatsappCountryCode: draft.whatsappCountryCode!,
            WhatsappNumber: draft.whatsappNumber!,
        };

        try {
            setSubmitLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;

            const res = await axios.post(
                `${apiUrl}/api/experience/booking`,
                payload
            );

            showNotification({
                title: "Order created",
                message: `Your experience is registered (ID: ${res.data.ExperienceBookingId}).`,
            });

            setActive(6);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Stack>
            {error && (
                <Alert color="red" title="Fejl">
                    {error}
                </Alert>
            )}

            <Stepper
                active={active}
                onStepClick={setActive}
                allowNextStepsSelect={false}
                orientation={isMobile ? "vertical" : "horizontal"}
                size={isMobile ? "sm" : "md"}
                styles={{
                    root: isMobile
                        ? { overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 6 }
                        : undefined,
                    steps: isMobile
                        ? { flexWrap: "nowrap", gap: 16 }
                        : undefined,
                    step: isMobile
                        ? { minWidth: 110 }
                        : undefined,
                }}
            >
                <Stepper.Step
                    label="Home"
                    description={isMobile ? (pickupName ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text><b>Name:</b> {init.person.name}</Text>
                        {init.person.email && <Text><b>Email:</b> {init.person.email}</Text>}
                        <Divider my="sm" />
                        <Text><b>Event:</b> {init.event.name}</Text>

                        <Divider my="sm" />

                        <Text fw={600} mb="xs">
                            WhatsApp number
                        </Text>

                        <Stack gap="sm">
                            <Select
                                data={whatsappCountryOptions}
                                searchable
                                value={draft.whatsappCountryIso2 ?? null}
                                onChange={(iso2) => {
                                    const c = WHATSAPP_COUNTRIES.find((x) => x.iso2 === iso2);
                                    setDraft((d) => ({
                                        ...d,
                                        whatsappCountryIso2: iso2 ?? undefined,
                                        whatsappCountryCode: c?.code,
                                    }));
                                }}
                            />

                            {draft.whatsappCountryCode && (
                                <Text size="sm" c="dimmed">
                                    Selected country: <b>{selectedWhatsappCountry ?? "Unknown"}</b>
                                </Text>
                            )}

                            <TextInput
                                label="WhatsApp number"
                                placeholder="e.g. 12345678"
                                value={draft.whatsappNumber ?? ""}
                                onChange={(e) => {
                                    const raw = e.currentTarget.value;
                                    // tillad tal + mellemrum (og evt. bindestreg) – du kan stramme den mere hvis du vil
                                    const cleaned = raw.replace(/[^\d\s-]/g, "");
                                    setDraft((d) => ({ ...d, whatsappNumber: cleaned }));
                                }}
                                required
                            />

                            {!whatsappValid && (
                                <Text size="sm" c="red">
                                    Please select a country code and enter your WhatsApp number to continue.
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label="Pick Up"
                    description={isMobile ? (pickupName ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Where should you be picked up?
                        </Text>
                        <OptionGrid
                            options={pickupOptions}
                            value={
                                draft.pickupLocationId
                                    ? String(draft.pickupLocationId)
                                    : null
                            }
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    pickupLocationId: Number(v),
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label="Drop Off"
                    description={isMobile ? (dropoffName ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Where are you going?
                        </Text>
                        <OptionGrid
                            options={dropoffOptions}
                            value={
                                draft.dropoffLocationId
                                    ? String(draft.dropoffLocationId)
                                    : null
                            }
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    dropoffLocationId: Number(v),
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label="Day"
                    description={isMobile ? ("" ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Select day
                        </Text>
                        <OptionGrid
                            options={dateOptions}
                            value={draft.pickupDate ?? null}
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    pickupDate: v,
                                    pickupTime: undefined,
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label="Time"
                    description={isMobile ? ("" ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Select a time slot
                        </Text>

                        {loadingTimes ? (
                            <Text>loading times…</Text>
                        ) : (
                            <SimpleGrid cols={{ base: 2, sm: 4 }}>
                                {times.map((t) => {
                                    const selected =
                                        draft.pickupTime === t;
                                    return (
                                        <Paper
                                            key={t}
                                            withBorder
                                            p="sm"
                                            radius="md"
                                            style={{
                                                cursor: "pointer",
                                                textAlign: "center",
                                                borderColor: selected
                                                    ? "var(--mantine-primary-color-filled)"
                                                    : undefined,
                                                boxShadow: selected
                                                    ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                                    : undefined,
                                            }}
                                            onClick={() =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    pickupTime: t,
                                                }))
                                            }
                                        >
                                            <Text fw={600}>{t}</Text>
                                        </Paper>
                                    );
                                })}
                            </SimpleGrid>
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label="Persons"
                    description={isMobile ? ("" ?? "Not selected") : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            How many people?
                        </Text>

                        <SimpleGrid cols={{ base: 5, sm: 10 }}>
                            {Array.from({ length: 30 }, (_, i) => i + 1).map(
                                (n) => {
                                    const selected =
                                        draft.passengerCount === n;
                                    return (
                                        <Paper
                                            key={n}
                                            withBorder
                                            p="xs"
                                            radius="md"
                                            style={{
                                                cursor: "pointer",
                                                textAlign: "center",
                                                borderColor: selected
                                                    ? "var(--mantine-primary-color-filled)"
                                                    : undefined,
                                            }}
                                            onClick={() =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    passengerCount: n,
                                                }))
                                            }
                                        >
                                            <Text fw={600}>{n}</Text>
                                        </Paper>
                                    );
                                }
                            )}
                        </SimpleGrid>
                    </Paper>
                </Stepper.Step>

                <Stepper.Completed>
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Confirmed ✅</Text>
                        <Text>
                            Thank you — your order has been registered.
                        </Text>
                        <Text>
                            WhatsApp: {draft.whatsappCountryCode} {draft.whatsappNumber}
                        </Text>
                    </Paper>
                </Stepper.Completed>
            </Stepper>

            {active < 6 && (
                <Group justify="space-between" mt="md">
                    <Button
                        variant="default"
                        onClick={prev}
                        disabled={active === 0}
                    >
                        Back
                    </Button>

                    {active === 5 ? (
                        <Button
                            onClick={submit}
                            loading={submitLoading}
                        >
                            Approve order
                        </Button>
                    ) : (
                        <Button
                            onClick={next}
                            disabled={!isStepValid(active)}
                        >
                            Next
                        </Button>
                    )}
                </Group>
            )}
        </Stack>
    );
}